// Настройки конкретного брокера поверх бренда.
//
// Бренд (REACT_APP_BRAND) — это палитра и логотип, и Horizon общий у Rox и JetQ.
// А вот что брокер продаёт и как называется — у каждого своё, поэтому отдельная
// переменная сборки:
//
//   REACT_APP_TENANT=        → как было (DOSCAR, Rox)
//   REACT_APP_TENANT=jetq    → JetQ: БУ-авто принимает только по договору
//                              поручения, своё название и иконка вкладки браузера
//   REACT_APP_TENANT=mega    → MEGA: как JetQ (серый дилер, только БУ-авто по
//                              договору поручения), своё название вкладки и без значка
import { CAR_ORIGIN_TYPES } from '../constants/carOrigin';

export const TENANTS = {
  jetq: {
    title: 'JetQ',
    // public/assets — иконка вкладки; у остальных остаётся apple-touch-icon.png.
    favicon: '/assets/jetq-icon.png',
    // Новые авто JetQ продаёт — разделы в «Автомобилях» и «Продажах» на месте,
    // как у DOSCAR (вернули 14.10.2026 по просьбе Димы).
    // Машины с пробегом JetQ принимает только по договору поручения; на новые
    // это ограничение не распространяется — список происхождения показывается
    // лишь в режиме «БУ авто».
    carOrigins: [CAR_ORIGIN_TYPES.CONSIGNMENT],
    // Серый дилер торгует любыми марками: менеджер заводит марку, модель и
    // комплектацию прямо в карточке авто, не дожидаясь администратора.
    managersAddDictionaries: true,
    // Трёхсторонний договор (Агент — Продавец — Покупатель) пока только у DOSCAR.
    // У JetQ все машины по договору поручения — без выключателя шаг встал бы в каждую сделку.
    tripartiteContract: false,
  },
  // Партнёры-серые дилеры заводятся по образцу MEGA: отличается только название
  // вкладки. Пока у всех одинаковый набор — только БУ-авто по договору поручения,
  // марки и модели менеджеры заводят сами. Если кто-то начнёт продавать новые
  // авто, у него появятся свои carOrigins, как было с JetQ.
  evmst: {
    title: 'EV-MASTER',
    favicon: null,
    usedCarsOnly: true,
    carOrigins: [CAR_ORIGIN_TYPES.CONSIGNMENT],
    tripartiteContract: false,
    managersAddDictionaries: true,
  },
  bayc: {
    title: 'Bay Cars',
    favicon: null,
    usedCarsOnly: true,
    carOrigins: [CAR_ORIGIN_TYPES.CONSIGNMENT],
    tripartiteContract: false,
    managersAddDictionaries: true,
  },
  tswg: {
    title: 'TSW Group',
    favicon: null,
    usedCarsOnly: true,
    carOrigins: [CAR_ORIGIN_TYPES.CONSIGNMENT],
    tripartiteContract: false,
    managersAddDictionaries: true,
  },
  drvpt: {
    title: 'Drive Point Auto',
    favicon: null,
    usedCarsOnly: true,
    carOrigins: [CAR_ORIGIN_TYPES.CONSIGNMENT],
    tripartiteContract: false,
    managersAddDictionaries: true,
  },
  jsavto: {
    title: 'JS Avto',
    favicon: null,
    usedCarsOnly: true,
    carOrigins: [CAR_ORIGIN_TYPES.CONSIGNMENT],
    tripartiteContract: false,
    managersAddDictionaries: true,
  },
  mega: {
    title: 'MEGA',
    // Своей иконки у партнёра нет, а наш логотип на его вкладке лишний:
    // null — это «убрать значок», а не «оставить как есть» (см. applyTenant).
    favicon: null,
    // MEGA — серый дилер, как JetQ: новых авто нет, машины только по договору
    // поручения, дилерский центр не привязан к марке.
    usedCarsOnly: true,
    carOrigins: [CAR_ORIGIN_TYPES.CONSIGNMENT],
    tripartiteContract: false,
    managersAddDictionaries: true,
  },
};

export const resolveTenant = (value) => {
  const key = String(value || '').trim().toLowerCase();
  return TENANTS[key] || null;
};

export const tenant = resolveTenant(process.env.REACT_APP_TENANT);

// Разделы «Новые авто» в меню «Автомобили» и «Продажи».
const NEW_CARS_PATHS = ['/cars/new', '/deals/new'];

// Раздел «Халык» в «Продажах»: Halyk Market подключён только у DOSCAR, у партнёров
// (серых дилеров) со своим tenant его нет.
const HALYK_PATHS = ['/deals/halyk'];

/**
 * Пункты меню «Автомобили» и «Продажи»: у брокера только с БУ-авто «Новые авто» убираем,
 * а раздел «Халык» оставляем только у DOSCAR (без tenant).
 */
export const usedOnlyMenuChildren = (children, current = tenant) =>
  current
    ? children.filter(
        (item) =>
          !HALYK_PATHS.includes(item.path) &&
          !(current.usedCarsOnly && NEW_CARS_PATHS.includes(item.path))
      )
    : children;

/** Варианты «Происхождение автомобиля»: у брокера со своим списком — только они. */
export const allowedOriginOptions = (options, current = tenant) =>
  current?.carOrigins ? options.filter((option) => current.carOrigins.includes(option.value)) : options;

/** Значок DOSCAR — для DOSCAR и Rox, у которых своего tenant нет. */
const DOSCAR_FAVICON = '/assets/apple-touch-icon.png';

/**
 * Название и иконка вкладки браузера.
 *
 * Значка в public/index.html нет: пока он там стоял, у партнёров логотип DOSCAR
 * успевал показаться до загрузки скрипта и оставался во вкладке (22.10.2026,
 * MEGA). Поэтому ставим его здесь — своим, а партнёрам не ставим вовсе.
 */
export const applyTenant = (doc = document, current = tenant) => {
  if (current?.title) doc.title = current.title;

  // null у партнёра — «значка нет», у DOSCAR и Rox tenant'а нет вовсе — значок свой.
  const favicon = current ? ('favicon' in current ? current.favicon : DOSCAR_FAVICON) : DOSCAR_FAVICON;
  const links = doc.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]');

  if (!favicon) {
    links.forEach((link) => link.remove());
    return;
  }
  if (links.length > 0) {
    links.forEach((link) => link.setAttribute('href', favicon));
    return;
  }
  const link = doc.createElement('link');
  link.setAttribute('rel', 'icon');
  link.setAttribute('href', favicon);
  doc.head.appendChild(link);
};

/** Может ли менеджер заводить марки, модели и комплектации сам (серые дилеры). */
export const managersAddDictionaries = (current = tenant) => current?.managersAddDictionaries === true;

/** Нужен ли шаг «Трёхсторонний договор» в сделках по договору поручения. */
export const tripartiteContractEnabled = (current = tenant) => current?.tripartiteContract !== false;
